import { renderTopbar } from '../components/topbar.js';
import { currentUser } from '../lib/auth.js';
import {
  watchWallet, rechargeWallet, formatPaise,
  ensureViewingAccess, fetchMyInvoices,
} from '../lib/wallet.js';
import { friendlyError } from '../lib/friendlyError.js';

// ─────────────────────────────────────────────────────────────────────────
// Wallet page (#/wallet) — balance, recharge, full ledger. The wallet is
// the only way to pay on the marketplace; the checkout dialog links here
// when the balance is short. Recharge opens real Razorpay Checkout
// (functions/wallet.js createRechargeOrder + verifyRecharge) — currently
// wired to the project's Razorpay TEST keys, so no real money moves yet,
// but the flow (order creation, hosted Checkout UI, signature-verified
// credit) is the exact same one that runs in production.
// ─────────────────────────────────────────────────────────────────────────

export function renderWallet(container) {
  container.innerHTML = '';
  container.appendChild(renderTopbar('/wallet'));

  const uid = currentUser().uid;
  const unsubs = [];

  const main = document.createElement('div');
  main.className = 'main-full';
  main.innerHTML = `
    <h1 style="margin-bottom:4px;">Wallet</h1>
    <p style="color:var(--text-dim);margin-top:0;">Manage your balance for Learning Lab access and creation.</p>

    <div class="settings-grid">
      <div class="settings-panel">
        <h3>Viewing access</h3>
        <p style="color:var(--text-dim);line-height:1.5;">Viewing your own content and Library items is billed separately from generation — once every 30 days, auto-debited from your wallet balance. Your first 30 days are free.</p>
        <div id="viewing-access-status"><div class="content-loading"><div class="content-loading-spinner"></div><p>Checking…</p></div></div>
      </div>

      <div class="settings-panel">
        <h3>Balance</h3>
        <p id="wallet-balance" style="font-size:2rem;font-weight:800;margin:6px 0 14px;">—</p>
        <div class="pill" style="margin-bottom:12px;">🧪 Recharges run through Razorpay TEST mode — use a <a href="https://razorpay.com/docs/payments/payments/test-card-upi-details/" target="_blank" rel="noopener">Razorpay test card/UPI</a>, no real money moves yet.</div>
        <div class="field">
          <label>Recharge amount (₹)</label>
          <input type="number" id="recharge-amount" min="1" placeholder="e.g. 500" />
        </div>
        <div id="recharge-error" style="color:var(--danger);font-size:0.85rem;min-height:1.2em;"></div>
        <button class="btn btn-primary" id="recharge-btn">Recharge wallet</button>
      </div>

      <div class="settings-panel" style="grid-column:1/-1;" id="school-invoices-panel" hidden>
        <h3>School invoices</h3>
        <p style="color:var(--text-dim);line-height:1.5;">Invoices issued by your school for your access. These are informational — your school collects payment directly. All amounts are inclusive of taxes.</p>
        <div id="school-invoices"></div>
      </div>

    </div>
  `;
  container.appendChild(main);

  // Also renews the period / attempts the monthly debit if due — same
  // on-demand check used before showing gated content elsewhere (see
  // lib/viewingAccess.js) — so opening the Wallet page right after a
  // recharge is enough to unblock viewing, no separate "retry" action.
  const viewingStatusEl = main.querySelector('#viewing-access-status');
  ensureViewingAccess().then((access) => {
    const statusLabel = {
      trialing: '🎁 Free trial',
      active: '✅ Active',
      blocked: '🔒 Blocked',
    }[access.viewingStatus] || (access.viewingStatus || 'Unknown');
    const dateLabel = access.viewingPeriodEndMs
      ? new Date(access.viewingPeriodEndMs).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : '—';
    viewingStatusEl.innerHTML = `
      <p style="margin:0 0 6px;font-weight:700;">${statusLabel}</p>
      <p style="color:var(--text-dim);margin:0;">
        ${access.blocked
          ? 'Your wallet balance was too low to renew. Recharge below, then reopen a topic or Library item.'
          : `${access.viewingStatus === 'trialing' ? 'Free trial ends' : 'Renews'} on ${dateLabel} — ₹${((access.costPaise || 0) / 100).toFixed(0)}/month after that, auto-debited from this wallet.`}
      </p>
    `;
  }).catch((err) => {
    viewingStatusEl.innerHTML = `<p style="color:var(--danger);margin:0;">${escapeHtml(friendlyError(err, 'Could not check viewing access.'))}</p>`;
  });

  // School invoices — the panel only appears when there is at least one.
  const invoicesPanel = main.querySelector('#school-invoices-panel');
  const invoicesEl = main.querySelector('#school-invoices');
  fetchMyInvoices(uid).then((invoices) => {
    if (!invoices.length) return;
    invoicesPanel.hidden = false;
    invoicesEl.innerHTML = `<div style="overflow:auto;"><table class="admin-table">
      <thead><tr><th>Invoice #</th><th>School</th><th>Issued</th><th style="text-align:right;">Platform charges</th><th style="text-align:right;">Instructor Charges</th><th style="text-align:right;">Total (incl. taxes)</th><th></th></tr></thead>
      <tbody>
        ${invoices.map((inv, i) => `<tr>
          <td>${escapeHtml(inv.invoiceNumber)}</td>
          <td>${escapeHtml(inv.schoolName)}</td>
          <td>${inv.issuedAtMs ? new Date(inv.issuedAtMs).toLocaleDateString() : '—'}</td>
          <td style="text-align:right;">${formatPaise(inv.platformPaise)}</td>
          <td style="text-align:right;">${formatPaise(inv.instructorPaise)}</td>
          <td style="text-align:right;font-weight:700;">${formatPaise(inv.totalPaise)}</td>
          <td><button class="btn btn-sm" data-print-invoice="${i}">View / print</button></td>
        </tr>`).join('')}
      </tbody>
    </table></div>`;
    invoicesEl.querySelectorAll('[data-print-invoice]').forEach((btn) => {
      btn.addEventListener('click', () => printInvoice(invoices[Number(btn.getAttribute('data-print-invoice'))]));
    });
  }).catch(() => { /* the panel simply stays hidden */ });

  const balanceEl = main.querySelector('#wallet-balance');
  unsubs.push(watchWallet(uid, ({ balancePaise }) => {
    balanceEl.textContent = formatPaise(balancePaise);
  }));

  const rechargeBtn = main.querySelector('#recharge-btn');
  rechargeBtn.addEventListener('click', async () => {
    const errEl = main.querySelector('#recharge-error');
    const input = main.querySelector('#recharge-amount');
    errEl.textContent = '';
    const amount = Number(input.value);
    if (!Number.isFinite(amount) || amount <= 0) {
      errEl.textContent = 'Enter a valid amount in rupees.';
      return;
    }
    rechargeBtn.disabled = true;
    rechargeBtn.textContent = 'Processing…';
    try {
      await rechargeWallet(amount);
      input.value = '';
      showToast('Wallet recharged.');
    } catch (err) {
      errEl.textContent = friendlyError(err, 'Recharge failed.');
    } finally {
      rechargeBtn.disabled = false;
      rechargeBtn.textContent = 'Recharge wallet';
    }
  });

  return () => unsubs.forEach((u) => u());
}

// A clean, print-friendly invoice in its own window — no PDF library
// needed; the browser's own print dialog handles paper/PDF output.
function printInvoice(inv) {
  const w = window.open('', '_blank', 'width=720,height=860');
  if (!w) return;
  const row = (label, amount, strong = false) => `
    <tr${strong ? ' style="font-weight:700;border-top:2px solid #222;"' : ''}>
      <td style="padding:8px 4px;">${label}</td>
      <td style="padding:8px 4px;text-align:right;">${formatPaise(amount)}</td>
    </tr>`;
  w.document.write(`<!DOCTYPE html><html><head><title>${escapeHtml(inv.invoiceNumber)}</title>
    <meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;color:#111;max-width:640px;margin:32px auto;padding:0 16px;}table{width:100%;border-collapse:collapse;}h1{font-size:1.3rem;}small{color:#555;}</style>
    </head><body>
    <h1>Invoice ${escapeHtml(inv.invoiceNumber)}</h1>
    <p><strong>${escapeHtml(inv.schoolName)}</strong> (code: ${escapeHtml(inv.schoolCode)})<br>
    <small>Billed to: ${escapeHtml(inv.studentEmail || '')}<br>
    Issued: ${inv.issuedAtMs ? new Date(inv.issuedAtMs).toLocaleDateString() : '—'}
    ${inv.termEndsAtMs ? ` · Access term ends: ${new Date(inv.termEndsAtMs).toLocaleDateString()}` : ''}</small></p>
    <table>
      ${row('Platform charges', inv.platformPaise)}
      ${row('Instructor Charges', inv.instructorPaise)}
      ${row('Total', inv.totalPaise, true)}
    </table>
    <p><small>${escapeHtml(inv.taxNote)}</small></p>
    <script>window.print();</script>
    </body></html>`);
  w.document.close();
}

function showToast(message) {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 3500);
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
